
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/layout/Layout';

export default function DistribuicaoPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [distributionMethod, setDistributionMethod] = useState<'manual' | 'csv'>('manual');
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  const [distributionType, setDistributionType] = useState('igual');
  const [totalPoints, setTotalPoints] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [customDistribution, setCustomDistribution] = useState<{[key: string]: string}>({});
  
  // Estados para importação CSV
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<Array<{
    participante: string;
    email: string;
    pontos: number;
    produto?: string;
    regra?: string;
    status: string;
  }>>([]);
  const [csvStep, setCsvStep] = useState<'upload' | 'preview' | 'validate'>('upload');
  
  const navigate = useNavigate();

  const campaigns = [
    {
      id: '1',
      name: 'Vendas Q4 2024',
      status: 'Ativa',
      participants: 45,
      pointsAvailable: 25000,
      description: 'Campanha de incentivo para vendas do último trimestre'
    },
    {
      id: '2',
      name: 'Meta Mensal Janeiro',
      status: 'Ativa',
      participants: 32,
      pointsAvailable: 18500,
      description: 'Metas mensais de performance e produtividade'
    },
    {
      id: '3',
      name: 'Indicação de Clientes',
      status: 'Ativa',
      participants: 67,
      pointsAvailable: 12000,
      description: 'Programa de indicação e captação de novos clientes'
    }
  ];

  const participants = [
    { id: '1', name: 'Ana Silva', department: 'Vendas', performance: 95, currentPoints: 1250 },
    { id: '2', name: 'Carlos Santos', department: 'Marketing', performance: 88, currentPoints: 980 },
    { id: '3', name: 'Maria Oliveira', department: 'Vendas', performance: 92, currentPoints: 1180 },
    { id: '4', name: 'João Costa', department: 'Suporte', performance: 85, currentPoints: 750 },
    { id: '5', name: 'Fernanda Lima', department: 'Vendas', performance: 97, currentPoints: 1420 },
    { id: '6', name: 'Roberto Alves', department: 'Marketing', performance: 90, currentPoints: 1050 }
  ];

  const steps = [
    { number: 1, title: 'Método', description: 'Escolha o método' },
    { number: 2, title: 'Campanha', description: 'Selecione a campanha' },
    { number: 3, title: distributionMethod === 'manual' ? 'Participantes' : 'Arquivo', description: distributionMethod === 'manual' ? 'Escolha os participantes' : 'Importe o arquivo' },
    { number: 4, title: 'Regras', description: 'Configure a distribuição' },
    { number: 5, title: 'Confirmação', description: 'Revise os dados' },
    { number: 6, title: 'Sucesso', description: 'Distribuição finalizada' }
  ];

  const distributionTypes = [
    {
      id: 'igual',
      name: 'Distribuição Igual',
      description: 'Divide os pontos igualmente entre todos os participantes',
      icon: 'ri-equalizer-line'
    },
    {
      id: 'performance',
      name: 'Por Performance',
      description: 'Distribui baseado na performance individual de cada participante',
      icon: 'ri-bar-chart-line'
    },
    {
      id: 'hierarquia',
      name: 'Por Hierarquia',
      description: 'Distribui baseado no nível hierárquico e responsabilidades',
      icon: 'ri-organization-chart'
    },
    {
      id: 'personalizada',
      name: 'Personalizada',
      description: 'Defina manualmente a quantidade de pontos para cada participante',
      icon: 'ri-settings-3-line'
    }
  ];

  const handleSelectAllParticipants = () => {
    if (selectedParticipants.length === participants.length) {
      setSelectedParticipants([]);
    } else {
      setSelectedParticipants(participants.map(p => p.id));
    }
  };

  const handleParticipantToggle = (participantId: string) => {
    setSelectedParticipants(prev => 
      prev.includes(participantId) 
        ? prev.filter(id => id !== participantId)
        : [...prev, participantId]
    );
  };

  const calculateDistribution = () => {
    const selectedCamp = campaigns.find(c => c.id === selectedCampaign);
    const points = parseInt(totalPoints) || (selectedCamp?.pointsAvailable || 0);
    const participantCount = selectedParticipants.length;

    if (distributionType === 'igual') {
      return Math.floor(points / participantCount);
    } else if (distributionType === 'performance') {
      const selectedParts = participants.filter(p => selectedParticipants.includes(p.id));
      const totalPerformance = selectedParts.reduce((sum, p) => sum + p.performance, 0);
      return selectedParts.map(p => ({
        id: p.id,
        points: Math.floor((p.performance / totalPerformance) * points)
      }));
    }
    return 0;
  };

  const handleNext = () => {
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    // Simular finalização da distribuição
    setCurrentStep(6);
  };

  // Funções para CSV
  const handleCsvFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      // Simular leitura do arquivo CSV
      const mockCsvData = [
        { participante: 'Ana Silva', email: 'ana.silva@empresa.com', pontos: 1500, produto: 'Smartphone', regra: 'Meta atingida', status: 'Válido' },
        { participante: 'Carlos Santos', email: 'carlos.santos@empresa.com', pontos: 1200, produto: 'Notebook', regra: 'Performance', status: 'Válido' },
        { participante: 'Maria Oliveira', email: 'maria.oliveira@empresa.com', pontos: 800, produto: 'Tablet', regra: 'Indicação', status: 'Válido' },
        { participante: 'João Costa', email: 'joao.costa@empresa.com', pontos: 2000, produto: 'Vale-compras', regra: 'Bônus especial', status: 'Válido' },
        { participante: 'Pedro Silva', email: 'pedro@empresa.com', pontos: 500, produto: '', regra: 'Meta parcial', status: 'Erro: Participante não encontrado' },
      ];
      setCsvPreview(mockCsvData);
      setCsvStep('preview');
    }
  };

  const downloadCsvTemplate = () => {
    const csvContent = 'participante,email,pontos,produto,regra\nAna Silva,ana.silva@empresa.com,1500,Smartphone,Meta atingida\nCarlos Santos,carlos.santos@empresa.com,1200,Notebook,Performance';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_distribuicao.csv';
    a.click();
  };

  const validateCsvData = () => {
    setCsvStep('validate');
  };

  const validCsvRecords = csvPreview.filter(r => r.status === 'Válido').length;
  const errorCsvRecords = csvPreview.filter(r => r.status.includes('Erro')).length;
  const totalCsvPoints = csvPreview.filter(r => r.status === 'Válido').reduce((sum, r) => sum + r.pontos, 0);

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Escolha o Método de Distribuição</h3>
              <p className="text-gray-600 mb-6">Selecione como deseja realizar a distribuição de pontos</p>
            </div>
            
            <div className="grid gap-6">
              <div
                className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                  distributionMethod === 'manual'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setDistributionMethod('manual')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      distributionMethod === 'manual' ? 'bg-blue-500' : 'bg-gray-100'
                    }`}>
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className={`ri-settings-3-line ${
                          distributionMethod === 'manual' ? 'text-white' : 'text-gray-600'
                        }`}></i>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900">Distribuição Manual</h4>
                      <p className="text-gray-600">Configure manualmente os participantes, regras e valores de distribuição</p>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-user-settings-line"></i>
                          </div>
                          <span>Seleção individual</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-equalizer-line"></i>
                          </div>
                          <span>Regras flexíveis</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    distributionMethod === 'manual'
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                  }`}>
                    {distributionMethod === 'manual' && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                </div>
              </div>

              <div
                className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                  distributionMethod === 'csv'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setDistributionMethod('csv')}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      distributionMethod === 'csv' ? 'bg-blue-500' : 'bg-gray-100'
                    }`}>
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className={`ri-file-excel-line ${
                          distributionMethod === 'csv' ? 'text-white' : 'text-gray-600'
                        }`}></i>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900">Importação via CSV</h4>
                      <p className="text-gray-600">Importe um arquivo CSV com participantes, valores, produtos e regras pré-definidas</p>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-upload-line"></i>
                          </div>
                          <span>Importação em lote</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-speed-line"></i>
                          </div>
                          <span>Processamento rápido</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    distributionMethod === 'csv'
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                  }`}>
                    {distributionMethod === 'csv' && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {distributionMethod === 'csv' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                    <i className="ri-information-line text-blue-600"></i>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-900 mb-2">Formato do Arquivo CSV</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• <strong>Colunas obrigatórias:</strong> participante, email, pontos</li>
                      <li>• <strong>Colunas opcionais:</strong> produto, regra</li>
                      <li>• <strong>Formato:</strong> UTF-8, separado por vírgulas</li>
                      <li>• <strong>Tamanho máximo:</strong> 10MB</li>
                    </ul>
                    <button
                      onClick={downloadCsvTemplate}
                      className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium cursor-pointer"
                    >
                      Baixar template de exemplo
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Selecione a Campanha</h3>
              <p className="text-gray-600 mb-6">Escolha qual campanha receberá a distribuição de pontos</p>
            </div>
            
            <div className="grid gap-4">
              {campaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedCampaign === campaign.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedCampaign(campaign.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-lg font-semibold text-gray-900">{campaign.name}</h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          campaign.status === 'Ativa' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {campaign.status}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-3">{campaign.description}</p>
                      <div className="flex items-center space-x-6 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-group-line"></i>
                          </div>
                          <span>{campaign.participants} participantes</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-coin-line"></i>
                          </div>
                          <span>{campaign.pointsAvailable.toLocaleString()} pontos disponíveis</span>
                        </div>
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedCampaign === campaign.id
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedCampaign === campaign.id && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 3:
        if (distributionMethod === 'csv') {
          return (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Importar Arquivo CSV</h3>
                <p className="text-gray-600 mb-6">Faça upload do arquivo CSV com os dados de distribuição</p>
              </div>

              {csvStep === 'upload' && (
                <div className="space-y-6">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className="ri-file-upload-line text-blue-600 text-2xl"></i>
                      </div>
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">Selecione o arquivo CSV</h4>
                    <p className="text-sm text-gray-600 mb-4">Arraste e solte ou clique para selecionar</p>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCsvFileSelect}
                      className="hidden"
                      id="csv-upload"
                    />
                    <label
                      htmlFor="csv-upload"
                      className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg cursor-pointer whitespace-nowrap"
                    >
                      Escolher Arquivo
                    </label>
                  </div>
                </div>
              )}

              {csvStep === 'preview' && (
                <div className="space-y-6">
                  {/* Resumo */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-file-list-line text-white"></i>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total de Registros</p>
                          <p className="text-xl font-bold text-gray-900">{csvPreview.length}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-checkbox-circle-line text-white"></i>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Válidos</p>
                          <p className="text-xl font-bold text-green-600">{validCsvRecords}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-error-warning-line text-white"></i>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Com Erros</p>
                          <p className="text-xl font-bold text-red-600">{errorCsvRecords}</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-coin-line text-white"></i>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total de Pontos</p>
                          <p className="text-xl font-bold text-purple-600">{totalCsvPoints.toLocaleString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tabela de Preview */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Participante</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pontos</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Regra</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {csvPreview.map((record, index) => (
                            <tr key={index} className={record.status.includes('Erro') ? 'bg-red-50' : ''}>
                              <td className="px-4 py-3 text-sm text-gray-900">{record.participante}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{record.email}</td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{record.pontos.toLocaleString()}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{record.produto || '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{record.regra || '-'}</td>
                              <td className="px-4 py-3">
                                {record.status === 'Válido' ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <div className="w-3 h-3 flex items-center justify-center mr-1">
                                      <i className="ri-checkbox-circle-fill"></i>
                                    </div>
                                    Válido
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    <div className="w-3 h-3 flex items-center justify-center mr-1">
                                      <i className="ri-error-warning-fill"></i>
                                    </div>
                                    Erro
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {errorCsvRecords > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                          <i className="ri-alert-line text-yellow-600"></i>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-yellow-900 mb-1">Atenção</h4>
                          <p className="text-sm text-yellow-800">
                            {errorCsvRecords} registro(s) contém erros e não será(ão) processado(s). 
                            Apenas os {validCsvRecords} registros válidos serão incluídos na distribuição.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        } else {
          // Conteúdo original para distribuição manual
          return (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Selecione os Participantes</h3>
                <p className="text-gray-600 mb-6">Escolha quais participantes receberão os pontos desta distribuição</p>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleSelectAllParticipants}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-checkbox-multiple-line"></i>
                    </div>
                    <span>
                      {selectedParticipants.length === participants.length ? 'Desmarcar Todos' : 'Selecionar Todos'}
                    </span>
                  </button>
                  <span className="text-sm text-gray-600">
                    {selectedParticipants.length} de {participants.length} selecionados
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    placeholder="Buscar participante..."
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-search-line text-gray-400"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Participante
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Departamento
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Performance
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Pontos Atuais
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Selecionar
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {participants.map((participant) => (
                        <tr key={participant.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-blue-600 font-medium text-sm">
                                  {participant.name.split(' ').map(n => n[0]).join('')}
                                </span>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{participant.name}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {participant.department}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className="text-sm font-medium text-gray-900 mr-2">{participant.performance}%</span>
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full"
                                  style={{ width: `${participant.performance}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {participant.currentPoints.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedParticipants.includes(participant.id)}
                              onChange={() => handleParticipantToggle(participant.id)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        }

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Configure as Regras de Distribuição</h3>
              <p className="text-gray-600 mb-6">
                {distributionMethod === 'csv' 
                  ? 'Revise as configurações para o processamento do arquivo CSV'
                  : 'Defina como os pontos serão distribuídos entre os participantes selecionados'
                }
              </p>
            </div>

            {distributionMethod === 'manual' && (
              <div className="grid gap-4 mb-6">
                {distributionTypes.map((type) => (
                  <div
                    key={type.id}
                    className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      distributionType === type.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setDistributionType(type.id)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        distributionType === type.id ? 'bg-blue-500' : 'bg-gray-100'
                      }`}>
                        <div className="w-6 h-6 flex items-center justify-center">
                          <i className={`${type.icon} ${
                            distributionType === type.id ? 'text-white' : 'text-gray-600'
                          }`}></i>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{type.name}</h4>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        distributionType === type.id
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {distributionType === type.id && (
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {distributionMethod === 'csv' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                <h4 className="font-semibold text-blue-900 mb-4">Resumo do Arquivo CSV</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700">Registros válidos:</span>
                    <span className="font-medium text-blue-900 ml-2">{validCsvRecords}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">Total de pontos:</span>
                    <span className="font-medium text-blue-900 ml-2">{totalCsvPoints.toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">Arquivo:</span>
                    <span className="font-medium text-blue-900 ml-2">{csvFile?.name}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">Tamanho:</span>
                    <span className="font-medium text-blue-900 ml-2">{csvFile ? (csvFile.size / 1024).toFixed(1) : 0} KB</span>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {distributionMethod === 'manual' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total de Pontos para Distribuir
                  </label>
                  <input
                    type="number"
                    value={totalPoints}
                    onChange={(e) => setTotalPoints(e.target.value)}
                    placeholder="Ex: 10000"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Deixe vazio para usar todos os pontos disponíveis da campanha
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Agendar Distribuição
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Deixe vazio para distribuir imediatamente
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="notification"
                checked={sendNotification}
                onChange={(e) => setSendNotification(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
              />
              <label htmlFor="notification" className="text-sm text-gray-700 cursor-pointer">
                Enviar notificação por email aos participantes
              </label>
            </div>

            {distributionMethod === 'manual' && distributionType === 'personalizada' && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-4">Distribuição Personalizada</h4>
                <div className="space-y-3">
                  {participants
                    .filter(p => selectedParticipants.includes(p.id))
                    .map((participant) => (
                      <div key={participant.id} className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">{participant.name}</span>
                        <input
                          type="number"
                          placeholder="Pontos"
                          value={customDistribution[participant.id] || ''}
                          onChange={(e) => setCustomDistribution(prev => ({
                            ...prev,
                            [participant.id]: e.target.value
                          }))}
                          className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        );

      case 5:
        const selectedCamp = campaigns.find(c => c.id === selectedCampaign);
        const selectedParts = distributionMethod === 'csv' 
          ? csvPreview.filter(r => r.status === 'Válido')
          : participants.filter(p => selectedParticipants.includes(p.id));
        const pointsToDistribute = distributionMethod === 'csv' 
          ? totalCsvPoints
          : parseInt(totalPoints) || (selectedCamp?.pointsAvailable || 0);
        
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirme a Distribuição</h3>
              <p className="text-gray-600 mb-6">Revise todos os dados antes de finalizar a distribuição de pontos</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Resumo da Campanha */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Resumo da Campanha</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Campanha:</span>
                    <span className="font-medium">{selectedCamp?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Método:</span>
                    <span className="font-medium">{distributionMethod === 'csv' ? 'Importação CSV' : 'Manual'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Participantes:</span>
                    <span className="font-medium">{selectedParts.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total de Pontos:</span>
                    <span className="font-medium">{pointsToDistribute.toLocaleString()}</span>
                  </div>
                  {distributionMethod === 'manual' && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tipo de Distribuição:</span>
                      <span className="font-medium">
                        {distributionTypes.find(t => t.id === distributionType)?.name}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Resumo Financeiro */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h4 className="font-semibold text-blue-900 mb-4">Resumo Financeiro</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-blue-700">Valor por Ponto:</span>
                    <span className="font-medium text-blue-900">R$ 0,30</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700">Valor Total:</span>
                    <span className="font-medium text-blue-900">
                      R$ {(pointsToDistribute * 0.30).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-blue-700">Valor Médio por Participante:</span>
                    <span className="font-medium text-blue-900">
                      R$ {((pointsToDistribute * 0.30) / selectedParts.length).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Lista de Participantes */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 mb-4">
                {distributionMethod === 'csv' ? 'Registros do CSV' : 'Participantes Selecionados'}
              </h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {distributionMethod === 'csv' ? (
                  csvPreview.filter(r => r.status === 'Válido').map((record, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-blue-600 font-medium text-xs">
                            {record.participante.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium text-gray-900">{record.participante}</span>
                          <span className="text-sm text-gray-500 ml-2">({record.email})</span>
                          {record.produto && (
                            <div className="text-xs text-gray-500">Produto: {record.produto}</div>
                          )}
                          {record.regra && (
                            <div className="text-xs text-gray-500">Regra: {record.regra}</div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-semibold text-blue-600">
                          +{record.pontos.toLocaleString()} pts
                        </span>
                        <div className="text-xs text-gray-500">
                          R$ {(record.pontos * 0.30).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  selectedParts.map((participant) => {
                    let pointsForParticipant = 0;
                    if (distributionType === 'igual') {
                      pointsForParticipant = Math.floor(pointsToDistribute / selectedParts.length);
                    } else if (distributionType === 'performance') {
                      const totalPerformance = selectedParts.reduce((sum, p) => sum + p.performance, 0);
                      pointsForParticipant = Math.floor((participant.performance / totalPerformance) * pointsToDistribute);
                    } else if (distributionType === 'personalizada') {
                      pointsForParticipant = parseInt(customDistribution[participant.id]) || 0;
                    }

                    return (
                      <div key={participant.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-medium text-xs">
                              {participant.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-gray-900">{participant.name}</span>
                            <span className="text-sm text-gray-500 ml-2">({participant.department})</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-blue-600">
                            +{pointsForParticipant.toLocaleString()} pts
                          </span>
                          <div className="text-xs text-gray-500">
                            R$ {(pointsForParticipant * 0.30).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Configurações Adicionais */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Configurações da Distribuição</h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-${scheduleDate ? 'calendar-line' : 'time-line'} text-gray-500`}></i>
                  </div>
                  <span className="text-gray-600">
                    {scheduleDate ? `Agendado para ${scheduleDate} às ${scheduleTime}` : 'Distribuição imediata'}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`ri-${sendNotification ? 'mail-line' : 'mail-close-line'} text-gray-500`}></i>
                  </div>
                  <span className="text-gray-600">
                    {sendNotification ? 'Notificação por email será enviada' : 'Sem notificação por email'}
                  </span>
                </div>
              </div>
            </div>

            {/* Termos */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                  <i className="ri-information-line text-yellow-600"></i>
                </div>
                <div>
                  <h4 className="font-medium text-yellow-800 mb-1">Importante</h4>
                  <p className="text-sm text-yellow-700">
                    Esta ação não pode ser desfeita. Os pontos serão distribuídos imediatamente aos participantes 
                    {distributionMethod === 'csv' ? ' conforme especificado no arquivo CSV' : ' selecionados'} 
                    e as notificações serão enviadas conforme configurado.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 6:
        const protocolNumber = `DIST-${Date.now().toString().slice(-8)}`;
        const currentDate = new Date().toLocaleDateString('pt-BR');
        const currentTime = new Date().toLocaleTimeString('pt-BR');
        
        return (
          <div className="text-center space-y-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <div className="w-10 h-10 flex items-center justify-center">
                <i className="ri-check-line text-green-600 text-3xl"></i>
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Distribuição Realizada com Sucesso!</h3>
              <p className="text-gray-600">
                Os pontos foram distribuídos {distributionMethod === 'csv' ? 'conforme arquivo CSV importado' : 'aos participantes selecionados'}
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-md mx-auto">
              <h4 className="font-semibold text-green-900 mb-4">Detalhes da Operação</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-green-700">Protocolo:</span>
                  <span className="font-medium text-green-900">{protocolNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Data:</span>
                  <span className="font-medium text-green-900">{currentDate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Horário:</span>
                  <span className="font-medium text-green-900">{currentTime}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Método:</span>
                  <span className="font-medium text-green-900">{distributionMethod === 'csv' ? 'Importação CSV' : 'Manual'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Pontos Distribuídos:</span>
                  <span className="font-medium text-green-900">
                    {distributionMethod === 'csv' ? totalCsvPoints.toLocaleString() : (parseInt(totalPoints) || 25000).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-700">Participantes:</span>
                  <span className="font-medium text-green-900">
                    {distributionMethod === 'csv' ? validCsvRecords : selectedParticipants.length}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-md mx-auto">
              <h4 className="font-semibold text-blue-900 mb-3">Próximos Passos</h4>
              <div className="space-y-2 text-sm text-blue-700">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-line"></i>
                  </div>
                  <span>Notificações enviadas aos participantes</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-file-text-line"></i>
                  </div>
                  <span>Comprovante disponível na seção Relatórios</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-bar-chart-line"></i>
                  </div>
                  <span>Acompanhe o impacto no Dashboard</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => {
                  setCurrentStep(1);
                  setDistributionMethod('manual');
                  setSelectedCampaign('');
                  setSelectedParticipants([]);
                  setTotalPoints('');
                  setScheduleDate('');
                  setScheduleTime('');
                  setCustomDistribution({});
                  setCsvFile(null);
                  setCsvPreview([]);
                  setCsvStep('upload');
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                Nova Distribuição
              </button>
              <button
                onClick={() => navigate('/admin/incentivo/pontuacao')}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Voltar ao Financeiro
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return distributionMethod !== '';
      case 2:
        return selectedCampaign !== '';
      case 3:
        if (distributionMethod === 'csv') {
          return csvStep === 'preview' && validCsvRecords > 0;
        } else {
          return selectedParticipants.length > 0;
        }
      case 4:
        if (distributionMethod === 'csv') {
          return true;
        } else {
          return distributionType !== '';
        }
      case 5:
        return true;
      default:
        return false;
    }
  };

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Distribuição de Pontos</h1>
            <p className="text-gray-600 mt-1">Distribua pontos aos participantes das campanhas de incentivo</p>
          </div>
          <button
            onClick={() => navigate('/admin/incentivo/pontuacao')}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            <span>Voltar</span>
          </button>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  currentStep > step.number
                    ? 'bg-green-500 border-green-500 text-white'
                    : currentStep === step.number
                    ? 'bg-blue-500 border-blue-500 text-white'
                    : 'bg-white border-gray-300 text-gray-500'
                }`}>
                  {currentStep > step.number ? (
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line"></i>
                    </div>
                  ) : (
                    <span className="text-sm font-medium">{step.number}</span>
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-1 mx-2 ${
                    currentStep > step.number ? 'bg-green-500' : 'bg-gray-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between">
            {steps.map((step) => (
              <div key={step.number} className="text-center" style={{ width: '120px' }}>
                <p className={`text-sm font-medium ${
                  currentStep >= step.number ? 'text-gray-900' : 'text-gray-500'
                }`}>
                  {step.title}
                </p>
                <p className={`text-xs ${
                  currentStep >= step.number ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        {currentStep < 6 && (
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className={`flex items-center space-x-2 px-6 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              <span>Voltar</span>
            </button>

            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">
                Etapa {currentStep} de {steps.length}
              </span>
              <button
                onClick={currentStep === 5 ? handleFinish : handleNext}
                disabled={!canProceed()}
                className={`flex items-center space-x-2 px-6 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  canProceed()
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <span>{currentStep === 5 ? 'Finalizar Distribuição' : 'Continuar'}</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-${currentStep === 5 ? 'check' : 'arrow-right'}-line`}></i>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
